import { useCallback, useRef } from 'react';
import { notifyAiLimit } from '@/shared/utils/apiError';
import { openOnboardingStream } from '../../replay';
import { SCAN_BASE } from '@utils/apiUtilsConstants';
import { parseScanCreateError, type StreamError } from './errors';

interface UseScanConnectionOptions {
  initialScanId?: string | null;
  websiteUrl?: string | null;
  companyName: string;
  businessType?: string | null;
  onMessage: (rawData: string) => void;
  onError: (err: StreamError) => void;
}

interface UseScanConnectionResult {
  /** Start or resume the scan (and open SSE). */
  start: () => Promise<void>;
  /** Close the SSE connection. */
  close: () => void;
  scanIdRef: React.MutableRefObject<string | null>;
}

export function useScanConnection({
  initialScanId,
  websiteUrl,
  companyName,
  businessType,
  onMessage,
  onError,
}: UseScanConnectionOptions): UseScanConnectionResult {
  const esRef = useRef<EventSource | null>(null);
  const scanIdRef = useRef<string | null>(initialScanId ?? null);

  const connect = useCallback((scanId: string) => {
    esRef.current?.close();
    const es = openOnboardingStream(
      `${SCAN_BASE}/${scanId}/stream`,
      { withCredentials: true },
      { secondary: true },
    );
    esRef.current = es;
    es.onmessage = (e) => onMessage(e.data as string);
    es.onerror = () => { /* browser auto-reconnects */ };
  }, [onMessage]);

  const start = useCallback(async () => {
    if (scanIdRef.current) {
      try {
        await fetch(`${SCAN_BASE}/${scanIdRef.current}/resume`, { method: 'POST', credentials: 'include' });
      } catch (err) {
        console.warn('[scan stream] resume request failed', err);
      }
      connect(scanIdRef.current);
      return;
    }
    if (!websiteUrl) return;
    try {
      const res = await fetch(SCAN_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ companyName, websiteUrl, businessType }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        notifyAiLimit(body);
        onError(parseScanCreateError(res.status, body));
        return;
      }
      const data = await res.json() as { scanId: string };
      scanIdRef.current = data.scanId;
      connect(data.scanId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Scan failed';
      console.error('[scan stream] start failed', err);
      onError({ source: 'stream', message, retryable: true });
    }
  }, [companyName, websiteUrl, businessType, connect, onError]);

  const close = useCallback(() => { esRef.current?.close(); }, []);

  return { start, close, scanIdRef };
}
